import { Container, Grid, Typography, Card, CardContent, Box, Chip, Button, CardHeader } from "@mui/material";
import styled, { keyframes } from "styled-components";
import InnerBanner from "../presentation/InnerBanner";
import SEO from "../global/SEO";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SchoolIcon from "@mui/icons-material/School";
import BookIcon from "@mui/icons-material/Book";
import BusinessIcon from "@mui/icons-material/Business";
import PublicIcon from "@mui/icons-material/Public";
import VerifiedIcon from "@mui/icons-material/Verified";
import SupportIcon from "@mui/icons-material/Support";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SecurityIcon from "@mui/icons-material/Security";

const Services = () => {
  const services = [
    {
      title: "Essay Writing Help",
      description: "Professional essay writing services for all academic levels. Get well-researched, original essays tailored to your requirements.",
      features: ["Custom research", "Proper citations", "Multiple revisions", "On-time delivery"],
      icon: <AssignmentIcon />,
      color: "#21a5a7",
      gradient: "linear-gradient(135deg, #21a5a7 0%, #1f2a62 100%)"
    },
    {
      title: "Dissertation Help",
      description: "Expert dissertation writing support for UK universities. Comprehensive research and proper formatting included.",
      features: ["UK university guidelines", "Original research", "Proper referencing", "Chapter-wise delivery"],
      icon: <SchoolIcon />,
      color: "#1f2a62",
      gradient: "linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%)"
    },
    {
      title: "Coursework Help",
      description: "Reliable coursework assistance for Australian students. University-specific guidance with 100% originality.",
      features: ["Australian standards", "Subject expertise", "Plagiarism-free", "Timely delivery"],
      icon: <BookIcon />,
      color: "#21a5a7",
      gradient: "linear-gradient(135deg, #21a5a7 0%, #1f2a62 100%)"
    },
    {
      title: "Case Study Help",
      description: "Professional case study analysis and writing for New Zealand students. Accurate and affordable solutions.",
      features: ["Detailed analysis", "Proper formatting", "Fast delivery", "Affordable pricing"],
      icon: <BusinessIcon />,
      color: "#1f2a62",
      gradient: "linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%)"
    },
    {
      title: "Assignment Help for Indian Students Abroad",
      description: "Specialized support for Indian students studying in Australia, UK, Canada, and New Zealand.",
      features: ["Cultural understanding", "Local standards", "24/7 support", "WhatsApp assistance"],
      icon: <PublicIcon />,
      color: "#21a5a7",
      gradient: "linear-gradient(135deg, #21a5a7 0%, #1f2a62 100%)"
    },
    {
      title: "Plagiarism-Free Assignments",
      description: "100% original, plagiarism-free assignments with comprehensive quality checks and guarantees.",
      features: ["Original content", "Quality assurance", "Free revisions", "Money-back guarantee"],
      icon: <VerifiedIcon />,
      color: "#1f2a62",
      gradient: "linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%)"
    }
  ];

  const features = [
    {
      title: "24/7 Support",
      description: "Available round the clock via WhatsApp, chat, and email",
      icon: <SupportIcon />,
      color: "#21a5a7"
    },
    {
      title: "100% Original",
      description: "Plagiarism-free content with comprehensive quality checks",
      icon: <SecurityIcon />,
      color: "#1f2a62"
    },
    {
      title: "On-Time Delivery",
      description: "Never miss deadlines with our guaranteed timely delivery",
      icon: <ScheduleIcon />,
      color: "#21a5a7"
    }
  ];

  return (
    <>
      <SEO 
        title="Assignment Writing Services | Essay, Dissertation & Coursework Help"
        description="Need help with essays, dissertations or coursework? Our academic experts provide tailored writing services at affordable prices. Chat with us now!"
        keywords="assignment writing services, essay help, dissertation help, coursework help, case study help, academic writing services, plagiarism-free assignments, assignment experts"
        h1Heading="Our Expert Assignment Writing Services – Built for Student Success"
        canonicalUrl="https://liveassignment.com/services"
      />
      <InnerBanner pageTitle="Our Services"></InnerBanner>
      <Wrapper>
        <Container>
          <HeaderSection>
            <Typography variant="h2" component="h1" align="center" gutterBottom>
              Our Expert Assignment Writing Services
            </Typography>
            <Typography variant="h6" align="center" color="textSecondary" paragraph>
              Professional academic support tailored to your specific needs and university requirements
            </Typography>
          </HeaderSection>
          
          <ServicesGrid container spacing={3}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <ServiceCard className="service-card">
                  <StyledCardHeader>
                    <IconWrapper gradient={service.gradient}>
                      {service.icon}
                    </IconWrapper>
                    <Typography variant="h5" component="h3" gutterBottom color="primary">
                      {service.title}
                    </Typography>
                  </StyledCardHeader>
                  <CardContent>
                    <Typography variant="body1" paragraph>
                      {service.description}
                    </Typography>
                    <FeaturesList>
                      {service.features.map((feature, idx) => (
                        <Chip 
                          key={idx} 
                          label={feature} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </FeaturesList>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      fullWidth 
                      sx={{ mt: 2 }}
                    >
                      Get Started
                    </Button>
                  </CardContent>
                </ServiceCard>
              </Grid>
            ))}
          </ServicesGrid>
          
          <FeaturesSection>
            <Typography variant="h4" align="center" gutterBottom>
              Why Choose Our Services?
            </Typography>
            <Grid container spacing={3}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} lg={4} key={index}>
                  <FeatureCard className="feature-card">
                    <FeatureIcon color={feature.color}>
                      {feature.icon}
                    </FeatureIcon>
                    <Typography variant="h6" color="primary" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" align="center">
                      {feature.description}
                    </Typography>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </FeaturesSection>
        </Container>
      </Wrapper>
    </>
  );
};

export default Services;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const Wrapper = styled.div`
  padding: 70px 0;
  position: relative;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);

  @media (max-width: 992px) {
    padding: 40px 0;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
  
  .MuiTypography-h2 {
    background: linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
`;

const ServicesGrid = styled(Grid)`
  margin-bottom: 80px;
  
  .MuiGrid-item {
    display: flex;
    margin-bottom: 20px;
  }
`;

const ServiceCard = styled(Card)`
  height: 100%;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: none;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }

  .MuiCardContent-root {
    padding: 30px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .MuiTypography-h5 {
    font-weight: 600;
    margin-bottom: 15px;
    text-align: center;
  }

  .MuiChip-root {
    font-size: 12px;
    height: 24px;
  }
`;

const StyledCardHeader = styled.div`
  text-align: center;
  padding: 30px 30px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  background: ${props => props.gradient};
  color: white;
  font-size: 40px;
  animation: ${pulse} 2s infinite;
  
  .MuiSvgIcon-root {
    font-size: 40px;
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 15px 0;
`;

const FeaturesSection = styled.div`
  text-align: center;
  padding: 60px 0;
  
  .MuiTypography-h4 {
    margin-bottom: 40px;
    background: linear-gradient(135deg, #1f2a62 0%, #21a5a7 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
  }
  
  .MuiGrid-item {
    display: flex;
    margin-bottom: 20px;
  }
`;

const FeatureCard = styled(Box)`
  text-align: center;
  padding: 40px 20px;
  border-radius: 20px;
  background: white;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0,0,0,0.15);
  }
  
  .MuiTypography-h6 {
    margin: 20px 0 15px;
    font-weight: 600;
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  background: ${props => props.color};
  color: white;
  font-size: 30px;
  
  .MuiSvgIcon-root {
    font-size: 30px;
  }
`; 